import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import QuoteForm from "@/components/forms/QuoteForm";
import {
  Phone,
  Star,
  Shield,
  Clock,
  Leaf,
  Users,
  CheckCircle2,
  Sparkles,
  Home as HomeIcon,
  Building2,
  ArrowRight,
  Award,
  ThumbsUp,
  MapPin,
} from "lucide-react";

const Home = () => {
  const services = [
    {
      icon: HomeIcon,
      title: "Standard Cleaning",
      description: "Regular maintenance cleaning to keep your home fresh and tidy every day.",
      price: "From $99",
    },
    {
      icon: Sparkles,
      title: "Deep Cleaning",
      description: "Thorough top-to-bottom cleaning for a spotless, sanitized home.",
      price: "From $199",
    },
    {
      icon: Building2,
      title: "Move-In/Out Cleaning",
      description: "Complete cleaning service for seamless property transitions.",
      price: "From $249",
    },
    {
      icon: Building2,
      title: "Office Cleaning",
      description: "Professional commercial cleaning for productive workspaces.",
      price: "Custom Quote",
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Vetted Professionals",
      description: "Background-checked, trained, and insured cleaning experts.",
    },
    {
      icon: Leaf,
      title: "Eco-Friendly Products",
      description: "Safe, non-toxic cleaning solutions for your family and pets.",
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Book online 24/7. Same-day and next-day availability.",
    },
    {
      icon: ThumbsUp,
      title: "100% Satisfaction",
      description: "Not happy? We'll re-clean for free. Guaranteed.",
    },
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      location: "Buckhead, Atlanta",
      rating: 5,
      text: "CartNexa transformed my home! The team was professional, thorough, and left everything sparkling. Highly recommend!",
    },
    {
      name: "Michael T.",
      location: "Midtown, Atlanta",
      rating: 5,
      text: "Been using their bi-weekly service for 6 months. Consistently excellent quality and the staff is always friendly.",
    },
    {
      name: "Jennifer L.",
      location: "Decatur, GA",
      rating: 5,
      text: "The deep cleaning service exceeded my expectations. My kitchen has never looked this clean!",
    },
  ];

  const faqs = [
    {
      question: "How much does house cleaning cost?",
      answer: "Our standard cleaning starts at $99 for apartments and small homes. Deep cleaning starts at $199. Pricing varies based on home size, condition, and specific services needed. Get a free, no-obligation quote today!",
    },
    {
      question: "Do I need to be home during the cleaning?",
      answer: "No, you don't need to be home. Many of our clients provide access via lockbox, garage code, or smart lock. Your belongings are safe with our insured and background-checked team.",
    },
    {
      question: "What cleaning products do you use?",
      answer: "We use eco-friendly, non-toxic cleaning products that are safe for children, pets, and the environment. If you have specific product preferences, let us know!",
    },
    {
      question: "How do I schedule or reschedule a cleaning?",
      answer: "You can book, reschedule, or cancel online 24/7 through our website or by calling us at +1-871-7871311. We ask for 24 hours notice for cancellations.",
    },
    {
      question: "Are your cleaners insured and background-checked?",
      answer: "Yes! All CartNexa cleaning professionals are thoroughly vetted, background-checked, and fully insured for your peace of mind.",
    },
  ];

  const serviceAreas = [
    "Atlanta", "Buckhead", "Midtown", "Decatur", "Sandy Springs",
    "Marietta", "Alpharetta", "Roswell", "Johns Creek", "Dunwoody"
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-primary/5 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-pattern opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold">
                <Award className="w-4 h-4" />
                #1 Rated Cleaning Service in Atlanta
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary leading-tight">
                Professional House Cleaning Services You Can Trust
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                Experience a cleaner, healthier home with Atlanta's most trusted cleaning professionals. We handle the mess so you can enjoy the rest.
              </p>
              
              {/* Trust Signals */}
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                  ))}
                  <span className="ml-2 font-bold text-slate-700">4.9</span>
                  <span className="text-slate-500 text-sm">(500+ reviews)</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link to="/#quote-form">
                  <Button className="h-14 px-8 bg-accent hover:bg-accent/90 text-white font-bold rounded-full text-lg btn-accent-hover" data-testid="hero-quote-btn">
                    Get a Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+18717871311">
                  <Button variant="outline" className="h-14 px-8 border-2 border-slate-200 text-slate-700 font-bold rounded-full text-lg hover:border-primary hover:text-primary" data-testid="hero-call-btn">
                    <Phone className="mr-2 w-5 h-5" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative animate-fade-in animation-delay-200">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Clean modern living room"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Starting from</p>
                      <p className="text-2xl font-bold text-secondary">$99</p>
                    </div>
                    <div className="flex items-center gap-2 text-primary">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">100% Satisfaction</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="bg-white py-12 border-y border-slate-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-primary">500+</p>
              <p className="text-slate-600 mt-1">Homes Cleaned Monthly</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-primary">4.9</p>
              <p className="text-slate-600 mt-1">Average Rating</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-primary">100%</p>
              <p className="text-slate-600 mt-1">Satisfaction Guarantee</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-primary">10+</p>
              <p className="text-slate-600 mt-1">Years Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Services</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              Professional Cleaning Solutions
            </h2>
            <p className="text-slate-600 mt-4">
              From regular maintenance to deep cleaning, we offer comprehensive services tailored to your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="bg-white border-slate-100 rounded-2xl card-hover" data-testid={`service-card-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-secondary mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">{service.description}</p>
                  <p className="text-primary font-bold">{service.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="h-12 px-8 border-2 border-primary text-primary font-bold rounded-full hover:bg-primary hover:text-white" data-testid="view-all-services-btn">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-bold text-primary uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
                Why Atlanta Trusts CartNexa
              </h2>
              <p className="text-slate-600 mt-4 mb-8">
                We're not just another cleaning company. We're your partners in creating a healthier, happier home environment.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-4" data-testid={`benefit-${index}`}>
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary">{benefit.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1729774091667-ff78f4da40f7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Professional cleaner at work"
                className="rounded-3xl shadow-xl w-full h-[500px] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-accent text-white p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-sm">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Results</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              See the CartNexa Difference
            </h2>
            <p className="text-slate-600 mt-4">
              Real transformations from real Atlanta homes. Your space deserves this level of clean.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <div className="relative">
                <span className="absolute top-4 left-4 bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold">Before</span>
                <img
                  src="https://images.pexels.com/photos/3787027/pexels-photo-3787027.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Kitchen before cleaning"
                  className="w-full h-[300px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <div className="relative">
                <span className="absolute top-4 left-4 bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold">After</span>
                <img
                  src="https://images.unsplash.com/photo-1636628751643-2a8c9fd001d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Kitchen after cleaning"
                  className="w-full h-[300px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Clean Spaces */}
      <section className="py-20 md:py-28 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Work</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              Spotless Spaces We've Created
            </h2>
            <p className="text-slate-600 mt-4">
              From kitchens to bedrooms, bathrooms to offices - we make every space shine.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Image 1 - Clean Living Room */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400&q=80"
                alt="Clean modern living room"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Living Room</span>
              </div>
            </div>

            {/* Image 2 - White Kitchen */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80"
                alt="Sparkling clean white kitchen"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Kitchen</span>
              </div>
            </div>

            {/* Image 3 - Clean Bathroom */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1620626011761-996317b8d101?w=400&q=80"
                alt="Clean white bathroom"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Bathroom</span>
              </div>
            </div>

            {/* Image 4 - Bright Bedroom */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=400&q=80"
                alt="Clean bright bedroom"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Bedroom</span>
              </div>
            </div>

            {/* Image 5 - Office Space */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80"
                alt="Clean modern office"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Office</span>
              </div>
            </div>

            {/* Image 6 - Cleaning Supplies */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1563453392212-326f5e854473?w=400&q=80"
                alt="Eco-friendly cleaning supplies"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Eco Products</span>
              </div>
            </div>

            {/* Image 7 - Dining Room */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1617806118233-18e1de247200?w=400&q=80"
                alt="Clean dining area"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Dining Room</span>
              </div>
            </div>

            {/* Image 8 - Apartment */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <img
                src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&q=80"
                alt="Clean apartment interior"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Apartment</span>
              </div>
            </div>

            {/* Image 9 - Hardwood Floors */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 col-span-2">
              <img
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80"
                alt="Polished hardwood floors"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Floor Cleaning</span>
              </div>
            </div>

            {/* Image 10 - Luxury Home */}
            <div className="relative group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 col-span-2">
              <img
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80"
                alt="Luxury clean home"
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white text-sm font-medium">Luxury Home</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Testimonials</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-slate-50 border-0 rounded-2xl" data-testid={`testimonial-${index}`}>
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-heading font-bold text-secondary">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 md:py-28 bg-primary/5" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Service Areas</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              Serving Greater Atlanta
            </h2>
            <p className="text-slate-600 mt-4">
              We proudly serve Atlanta and surrounding communities with professional cleaning services.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm"
              >
                <MapPin className="w-4 h-4 text-primary" />
                <span className="text-slate-700 font-medium">{area}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/service-areas">
              <Button variant="link" className="text-primary font-bold">
                View All Service Areas <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">FAQ</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="bg-slate-50 rounded-2xl border-0 px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-bold text-secondary hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-20 md:py-28 bg-slate-50" data-testid="quote-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-bold text-primary uppercase tracking-wider">Get Started</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
                Get Your Free Quote Today
              </h2>
              <p className="text-slate-600 mt-4 mb-8">
                Fill out the form and we'll get back to you within 24 hours with a customized quote for your cleaning needs.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-slate-700">No obligation quote</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-slate-700">Response within 24 hours</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-slate-700">Competitive pricing guaranteed</span>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white rounded-2xl shadow-sm">
                <p className="text-slate-600 mb-2">Prefer to talk?</p>
                <a href="tel:+18717871311" className="flex items-center gap-2 text-2xl font-bold text-primary hover:text-primary/80 transition-colors" data-testid="quote-section-phone">
                  <Phone className="w-6 h-6" />
                  +1-871-7871311
                </a>
              </div>
            </div>

            <Card className="bg-white border-slate-100 rounded-3xl shadow-xl">
              <CardContent className="p-8">
                <QuoteForm />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-secondary" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready for a Spotless Home?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of happy Atlanta homeowners who trust CartNexa for their cleaning needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#quote-form">
              <Button className="h-14 px-8 bg-accent hover:bg-accent/90 text-white font-bold rounded-full text-lg btn-accent-hover" data-testid="final-cta-quote-btn">
                Book Your Cleaning
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18717871311">
              <Button variant="outline" className="h-14 px-8 border-2 border-white text-white font-bold rounded-full text-lg hover:bg-white hover:text-secondary" data-testid="final-cta-call-btn">
                <Phone className="mr-2 w-5 h-5" />
                +1-871-7871311
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
